import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import {
  type TranslationRefKey,
  translationReferencesAdapter,
} from '@polyai/schemas';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

export { translationReferencesAdapter };

import {
  BEHAVIOUR_REFERENCE_KEY,
  DISCLAIMER_REFERENCE_KEY,
  GREETING_REFERENCE_KEY,
  VOICE_DISCLAIMER_REFERENCE_KEY,
} from '@polyai/stream/store/utils/reference-helpers';

import {
  updateDisclaimerMessage,
  updateGreeting,
  updateRules,
} from '../agent-settings/actions';
import {
  updateChannelGreeting,
  updateVoiceChannelDisclaimer,
} from '../channels/actions';
import { createTopic, updateTopic } from '../knowledge-base/actions';
import { addLanguage } from '../languages/actions';
import { createTemplate, updateTemplate } from '../sms/actions';
import { getChannelGreetingReferenceKey } from '../variables/utils';
import {
  createTranslation,
  deleteTranslation,
  updateTranslation,
} from './actions';
import type { TranslationEntry, Translations } from './types';

function syncTranslationRefs(
  state: Translations,
  refs: Record<string, unknown> | undefined,
  refKey: TranslationRefKey,
  entityId: string,
  options: { removeStale?: boolean } = {},
): void {
  if (!refs) {
    return;
  }
  for (const translationId of Object.keys(refs)) {
    translationReferencesAdapter.addReference(
      state.translations,
      translationId,
      refKey,
      entityId,
    );
  }
  if (options.removeStale) {
    for (const translation of Object.values(state.translations.entities)) {
      if (
        translation?.references?.[refKey]?.[entityId] !== undefined &&
        refs[translation.id] === undefined
      ) {
        translationReferencesAdapter.removeReference(
          state.translations,
          translation.id,
          refKey,
          entityId,
        );
      }
    }
  }
}

export const translationsAdapter = createEntityAdapter<TranslationEntry>();

export const translationsSelectors = translationsAdapter.getSelectors();

const initialState: Translations = {
  translations: translationsAdapter.getInitialState(),
};

const slice = createSlice({
  name: 'translations',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(createTranslation, (state, action) => {
      const entity: TranslationEntry = {
        ...action.payload,
        translations: action.payload.translations.map(tn => ({
          ...tn,
        })),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
        references: translationReferencesAdapter.createInitialState(),
      };

      assignMetadata(action, entity);
      translationsAdapter.addOne(state.translations, entity);
    });

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing complex logic
    builder.addCase(updateTranslation, ({ translations }, action) => {
      const translationEntry = translations.entities?.[action.payload.id];

      if (!translationEntry) {
        return;
      }

      if (action.payload.translationKey) {
        translationEntry.translationKey = action.payload.translationKey;
      }

      if (
        action.payload.translations &&
        Array.isArray(action.payload.translations)
      ) {
        for (const tn of action.payload.translations) {
          const toUpdateTn = translationEntry.translations.find(
            t => t.languageCode === tn.languageCode,
          );

          if (!toUpdateTn) {
            continue;
          }

          if (
            tn.isAutoTranslated !== undefined &&
            tn.isAutoTranslated !== null
          ) {
            toUpdateTn.isAutoTranslated = tn.isAutoTranslated;
          }

          if (tn.text !== undefined && tn.text !== null) {
            toUpdateTn.text = tn.text;
          }
        }
      }

      assignMetadata(action, translationEntry);
    });

    builder.addCase(deleteTranslation, (state, action) => {
      translationsAdapter.removeOne(state.translations, action.payload.id);
    });

    // Add a new language entry to all existing translations
    builder.addCase(addLanguage, (state, action) => {
      const languageCode = action.payload.code;
      for (const translation of Object.values(state.translations.entities)) {
        if (!translation) {
          continue;
        }
        const alreadyExists = translation.translations.some(
          t => t.languageCode === languageCode,
        );
        if (!alreadyExists) {
          translation.translations.push({
            languageCode,
            text: '',
            isAutoTranslated: false,
          });
        }
      }
    });

    // Topic references
    builder.addCase(createTopic, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'topics',
        action.payload.id,
      );
    });
    builder.addCase(updateTopic, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'topics',
        action.payload.id,
        { removeStale: true },
      );
    });

    // SMS references
    builder.addCase(createTemplate, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'sms',
        action.payload.id,
      );
    });
    builder.addCase(updateTemplate, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'sms',
        action.payload.id,
        { removeStale: true },
      );
    });

    // Greeting references
    builder.addCase(updateGreeting, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'greetings',
        GREETING_REFERENCE_KEY,
        { removeStale: true },
      );
    });

    builder.addCase(updateChannelGreeting, (state, action) => {
      const { channelType, greeting } = action.payload;
      if (!greeting) {
        return;
      }
      syncTranslationRefs(
        state,
        greeting.references?.translations,
        'greetings',
        getChannelGreetingReferenceKey(channelType),
        { removeStale: true },
      );
    });

    // Disclaimer message references
    builder.addCase(updateDisclaimerMessage, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'disclaimerMessages',
        DISCLAIMER_REFERENCE_KEY,
        { removeStale: true },
      );
    });

    builder.addCase(updateVoiceChannelDisclaimer, (state, action) => {
      const { disclaimer } = action.payload;
      if (!disclaimer) {
        return;
      }
      syncTranslationRefs(
        state,
        disclaimer.references?.translations,
        'disclaimerMessages',
        VOICE_DISCLAIMER_REFERENCE_KEY,
        { removeStale: true },
      );
    });

    // Rules references
    builder.addCase(updateRules, (state, action) => {
      syncTranslationRefs(
        state,
        action.payload.references?.translations,
        'behaviour',
        BEHAVIOUR_REFERENCE_KEY,
        { removeStale: true },
      );
    });
  },
});

export default slice.reducer;
